import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Link,
  makeStyles,
  Menu,
  MenuItem,
  Theme,
} from '@material-ui/core';
import { IoMdInformationCircle, IoMdStar } from 'react-icons/io';
import { MdCancel, MdForwardToInbox, MdMoreVert } from 'react-icons/md';
import { RespostasI } from '../../interfaces/form-flow';
import { EvaluationDialog } from '../EvaluationDialog';
import { RestitutionSelected } from './types';

type EvaluationData = {
  avaliacao_atendimento: number;
  avaliacao_informacao: number;
  comentario_atendimento: null | string;
  comentario_informacao: null | string;
  id: string;
  pendente: boolean;
} | null;

type RestitutionData = {
  anexos_restituicao: [];
  historico: number;
  id: string;
  respondido: boolean;
  resposta: string | null;
  solicitacao: number;
};

interface Props {
  request: RespostasI;
  onLoadRequests: ({ page }: { page?: number }) => void;
  onViewDetails?: () => void;
  handleOpenModal?: () => void;
  onOpenModalCancel?: () => void;
  setRequestId?: (value: number) => void;
  evaluationData?: EvaluationData;
  restitutionData?: RestitutionData[] | null;
  setDataRestitution?: (value: RestitutionSelected) => void;
  compact?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  boxActionsIcon: {
    width: 40,
    height: 40,
    borderRadius: 4,
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
  },
  icon: {
    height: 24,
    width: 24,
  },
  iconCancel: {
    height: 24,
    width: 24,
    color: '#f00',
  },
  links: {
    display: 'flex',
    gap: 10,
    fontFamily: 'Roboto',
    alignItems: 'center',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 500,
    color: '#373F41',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.typography.pxToRem(13),
    color: '#373F41',
    fontSize: theme.typography.pxToRem(16),
    fontFamily: 'Roboto',
    fontWeight: 500,
  },
  menuIcon: {
    fontSize: theme.typography.pxToRem(20),
  },
  boxActionsIconCompact: {
    width: 40,
    height: 32,
    borderRadius: 4,
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
  },
}));

export default function ButtonActions({
  request,
  onLoadRequests,
  onViewDetails,
  handleOpenModal = () => undefined,
  onOpenModalCancel,
  setRequestId,
  evaluationData = null,
  restitutionData = null,
  setDataRestitution,
  compact = false,
}: Props): JSX.Element {
  const classes = useStyles();
  const [openDialog, setOpenDialog] = useState(false);
  const preventDefault = (event: React.SyntheticEvent) =>
    event.preventDefault();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
  }, []);

  const handleCloseMenuAndDialog = () => {
    setAnchorEl(null);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const currentStatus = request.status;

  const isActiveButtonCancel = useMemo(
    () =>
      currentStatus === 'Finalizado' ||
      currentStatus === 'Cancelado pelo atendente' ||
      currentStatus === 'Cancelado pelo usuário',
    [currentStatus],
  );

  const isActiveButtonEvaluation = useMemo(
    () => Boolean(evaluationData?.pendente),
    [evaluationData],
  );

  const showActionsButton = useMemo(
    () =>
      Boolean(onViewDetails) ||
      isActiveButtonEvaluation ||
      !isActiveButtonCancel,
    [isActiveButtonCancel, isActiveButtonEvaluation, onViewDetails],
  );

  const onUpdatedList = useCallback(
    () => onLoadRequests({ page: 1 }),
    [onLoadRequests],
  );

  return (
    <>
      {showActionsButton && (
        <Box>
          <Box
            className={
              compact ? classes.boxActionsIconCompact : classes.boxActionsIcon
            }
          >
            <IconButton
              aria-label='ações da solicitação'
              aria-haspopup='true'
              onClick={handleClick}
            >
              <MdMoreVert className={classes.icon} style={{ color: '#fff' }} />
            </IconButton>
          </Box>

          <Menu
            id='simple-menu'
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {onViewDetails && (
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  handleClose();
                  onViewDetails();
                }}
              >
                <IoMdInformationCircle className={classes.menuIcon} />
                <span>Informações</span>
              </MenuItem>
            )}

            {restitutionData?.map(restituicao => {
              if (!restituicao.respondido) {
                return (
                  <MenuItem
                    key={restituicao.id}
                    onClick={() => {
                      handleOpenModal();
                      setDataRestitution?.({
                        solicitacao: restituicao.solicitacao,
                        id: restituicao.id,
                      });
                      handleClose();
                    }}
                  >
                    <Link
                      href='#'
                      onClick={preventDefault}
                      className={classes.links}
                    >
                      <MdForwardToInbox className={classes.icon} />
                      Responder
                    </Link>
                  </MenuItem>
                );
              }
              return null;
            })}

            {evaluationData?.pendente && (
              <MenuItem
                className={classes.menuItem}
                onClick={handleCloseMenuAndDialog}
              >
                <IoMdStar className={classes.menuIcon} />
                <span>Avaliar consumo</span>
              </MenuItem>
            )}

            {!isActiveButtonCancel && onOpenModalCancel && setRequestId && (
              <MenuItem
                onClick={() => {
                  onOpenModalCancel();
                  setRequestId(request.id);
                  handleClose();
                }}
              >
                <Link className={classes.links}>
                  <MdCancel className={classes.iconCancel} />
                  Cancelar solicitação
                </Link>
              </MenuItem>
            )}
          </Menu>
        </Box>
      )}

      {evaluationData?.id && (
        <EvaluationDialog
          open={openDialog}
          handleCloseOrOpenDialog={handleCloseDialog}
          type='formflow'
          evaluationIndicator={evaluationData.id}
          handleUpdated={onUpdatedList}
        />
      )}
    </>
  );
}
